import { useState, useEffect } from 'react';
import { useProjects } from '@/hooks/useProjects';
import { useProjectJobs } from '@/hooks/useProjectJobs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ProjectDialog } from '@/components/projects/ProjectDialog';
import { DeleteProjectDialog } from '@/components/projects/DeleteProjectDialog';
import { AddJobsToProjectDialog } from '@/components/projects/AddJobsToProjectDialog';
import { Project, ProjectFormData } from '@/lib/types/project';
import { Plus, Search, Edit, Trash2, FolderPlus, GripVertical, ExternalLink, X } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableProjectItem({ project, children }: { project: Project; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center gap-2">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-accent rounded">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const { projects, loading, createProject, updateProject, deleteProject, reorderProject, fetchProjects } = useProjects();
  const { jobs: orphanedJobs, fetchOrphanedJobs, addJobToProject, removeJobFromProject } = useProjectJobs();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addJobsDialogOpen, setAddJobsDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [organizationUsers, setOrganizationUsers] = useState<any[]>([]);
  const [expandedProjects, setExpandedProjects] = useState<string[]>([]);
  const [projectJobs, setProjectJobs] = useState<Record<string, any[]>>({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchOrganizationUsers();
    fetchOrphanedJobs();
  }, []);

  const fetchOrganizationUsers = async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile?.organization_id) return;

      // Get all users in this organization
      const { data: orgProfiles } = await supabase
        .from('profiles')
        .select('user_id, role')
        .eq('organization_id', profile.organization_id);

      if (!orgProfiles) {
        setOrganizationUsers([]);
        return;
      }

      // Fetch names from role-specific tables
      const userIds = orgProfiles.map(p => p.user_id);
      
      const [{ data: clientProfiles }, { data: vendorProfiles }] = await Promise.all([
        supabase
          .from('client_profiles')
          .select('user_id, first_name, last_name')
          .in('user_id', userIds),
        supabase
          .from('vendor_profiles_enhanced')
          .select('user_id, first_name, last_name')
          .in('user_id', userIds)
      ]);

      // Merge the data
      const usersWithNames = orgProfiles.map(p => {
        const clientProfile = clientProfiles?.find(cp => cp.user_id === p.user_id);
        const vendorProfile = vendorProfiles?.find(vp => vp.user_id === p.user_id);
        return {
          user_id: p.user_id,
          first_name: clientProfile?.first_name || vendorProfile?.first_name || null,
          last_name: clientProfile?.last_name || vendorProfile?.last_name || null,
        };
      });

      setOrganizationUsers(usersWithNames);
    } catch (error) {
      console.error('Error fetching organization users:', error);
    }
  };

  const fetchJobsForProject = async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('project_id', projectId)
        .order('project_display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjectJobs((prev) => ({ ...prev, [projectId]: data || [] }));
    } catch (error) {
      console.error('Error fetching jobs for project:', error);
    }
  };

  const handleAccordionChange = (value: string[]) => {
    setExpandedProjects(value);
    
    // Fetch jobs for newly expanded projects
    value.forEach((projectId) => {
      if (!projectJobs[projectId]) {
        fetchJobsForProject(projectId);
      }
    });
  };

  const filteredProjects = projects
    .filter((project) => {
      const matchesSearch =
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.project_code?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'jobs':
          return b.job_count - a.job_count;
        default:
          return 0;
      }
    });

  const handleSaveProject = async (data: ProjectFormData) => {
    if (selectedProject) {
      await updateProject(selectedProject.id, data);
    } else {
      await createProject(data);
    }
  };

  const handleDeleteProject = async () => {
    if (selectedProject) {
      await deleteProject(selectedProject.id);
    }
  };

  const handleAddJobsToProject = async (jobIds: string[]) => {
    if (!selectedProject) return;
    
    for (const jobId of jobIds) {
      await addJobToProject(jobId, selectedProject.id);
    }
    
    await fetchJobsForProject(selectedProject.id);
    await fetchOrphanedJobs();
    await fetchProjects();
  };

  const handleRemoveJob = async (jobId: string, projectId: string) => {
    await removeJobFromProject(jobId);
    await fetchJobsForProject(projectId);
    await fetchOrphanedJobs();
    await fetchProjects();
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredProjects.findIndex((p) => p.id === active.id);
      const newIndex = filteredProjects.findIndex((p) => p.id === over.id);

      const reorderedProjects = arrayMove(filteredProjects, oldIndex, newIndex);

      // Update display_order for all affected projects
      for (let i = 0; i < reorderedProjects.length; i++) {
        await reorderProject(reorderedProjects[i].id, i);
      }
    }
  };

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'completed':
        return 'secondary';
      case 'on_hold':
        return 'outline';
      case 'archived':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getJobStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'draft':
        return 'secondary';
      case 'rfq_open':
        return 'default';
      case 'awarded':
      case 'in_progress':
        return 'default';
      case 'completed_pending_review':
      case 'accepted':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <div className="text-center py-8">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-7xl">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl sm:text-3xl font-bold">Projects</h1>
          <Button onClick={() => { setSelectedProject(undefined); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Project
          </Button>
        </div>
        <p className="text-sm sm:text-base text-muted-foreground">
          Organize your jobs into project folders
        </p>
      </div>

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger>
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="created">Created Date</SelectItem>
              <SelectItem value="jobs">Job Count</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {filteredProjects.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">
            {projects.length === 0 ? 'No projects yet. Create your first project to get started.' : 'No projects match your filters.'}
          </p>
          {projects.length === 0 && (
            <Button onClick={() => { setSelectedProject(undefined); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Project
            </Button>
          )}
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredProjects.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <Accordion type="multiple" value={expandedProjects} onValueChange={handleAccordionChange} className="space-y-4">
              {filteredProjects.map((project) => (
                <SortableProjectItem key={project.id} project={project}>
                  <AccordionItem value={project.id} className="border rounded-lg overflow-hidden">
                    <Card className="border-0">
                      <AccordionTrigger className="hover:no-underline px-6 py-4">
                        <div className="flex items-center gap-4 flex-1 text-left">
                          <div
                            className="w-1 h-12 rounded-full flex-shrink-0"
                            style={{ backgroundColor: project.color || '#3B82F6' }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-lg truncate">{project.name}</span>
                              {project.project_code && (
                                <Badge variant="outline" className="font-mono text-xs">
                                  {project.project_code}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                              <Badge variant={getStatusBadgeVariant(project.status)}>
                                {project.status?.replace(/_/g, ' ') || 'active'}
                              </Badge>
                              <span className="flex items-center gap-1">
                                <span className="font-medium">{project.job_count}</span> job{project.job_count !== 1 ? 's' : ''}
                              </span>
                              {project.start_date && project.end_date && (
                                <span>
                                  {format(new Date(project.start_date), 'MMM d')} - {format(new Date(project.end_date), 'MMM d, yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProject(project);
                                setDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProject(project);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4">
                        {project.description && (
                          <p className="text-sm text-muted-foreground mb-4">{project.description}</p>
                        )}

                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-muted/50 px-4 py-2 flex items-center justify-between">
                            <span className="text-sm font-medium">Jobs in this project</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedProject(project);
                                setAddJobsDialogOpen(true);
                              }}
                            >
                              <FolderPlus className="h-4 w-4 mr-2" />
                              Add Jobs
                            </Button>
                          </div>
                          {projectJobs[project.id] && projectJobs[project.id].length > 0 ? (
                            <div className="divide-y">
                              {projectJobs[project.id].map((job) => (
                                <div key={job.id} className="px-4 py-3 hover:bg-accent/50 flex items-center justify-between group">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Link
                                        to={`/jobs/${job.public_id}`}
                                        className="font-medium hover:underline truncate"
                                      >
                                        {job.title}
                                      </Link>
                                      <Badge variant={getJobStatusBadgeVariant(job.status)} className="text-xs">
                                        {job.status.replace(/_/g, ' ')}
                                      </Badge>
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      <span className="font-mono">{job.public_id}</span>
                                      {job.due_date && (
                                        <span className="ml-2">
                                          • Due: {format(new Date(job.due_date), 'MMM d, yyyy')}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button variant="ghost" size="icon" asChild>
                                      <Link to={`/jobs/${job.public_id}`}>
                                        <ExternalLink className="h-4 w-4" />
                                      </Link>
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleRemoveJob(job.id, project.id)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                              No jobs in this project yet
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </Card>
                  </AccordionItem>
                </SortableProjectItem>
              ))}
            </Accordion>
          </SortableContext>
        </DndContext>
      )}

      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        project={selectedProject}
        onSave={handleSaveProject}
        organizationUsers={organizationUsers}
      />

      {selectedProject && (
        <>
          <DeleteProjectDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            project={selectedProject}
            jobCount={projectJobs[selectedProject.id]?.length || 0}
            onConfirm={handleDeleteProject}
          />

          <AddJobsToProjectDialog
            open={addJobsDialogOpen}
            onOpenChange={setAddJobsDialogOpen}
            projectId={selectedProject.id}
            projectName={selectedProject.name}
            orphanedJobs={orphanedJobs}
            onAddJobs={handleAddJobsToProject}
          />
        </>
      )}
    </div>
  );
}
